import React from 'react';
import { ArrowLeft, Users, Target, CheckCircle, FileText, Calendar, Award, Mail, Phone, Globe, BookOpen, Clock, DollarSign, Download } from 'lucide-react';
import { Link } from 'react-router-dom';

const Aspirantes = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <Link
            to="/maestria"
            className="inline-flex items-center gap-2 text-green-200 hover:text-white mb-6 transition-colors duration-300"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver a Maestría
          </Link>
          <div className="flex items-center gap-4">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <Users className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2">Información para Aspirantes</h1>
              <p className="text-xl text-green-100">
                Todo lo que necesitas saber para estudiar en el CENID
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">

        {/* ¿Cómo se estudia? */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <BookOpen className="w-8 h-8 text-[#0b742f]" />
              <h2 className="text-2xl font-bold text-gray-900">¿Cómo se estudia?</h2>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="text-base leading-relaxed mb-6">
                Al inscribirse un alumno (a) al CENID se le hace llegar una clave de acceso a la plataforma
                de cursos en línea localizados en <strong>postgrados.cenid.org</strong> con la cual tendrá acceso a las
                asignaturas, guías autoinstruccionales, bibliografía, donde se señalan las actividades de
                aprendizaje en las que este registrado durante el semestre.
              </p>

              <p className="text-base leading-relaxed mb-6">
                Los alumnos (as) de cualquier programa educativo de CENID deben cursar y acreditar el total
                de asignaturas. Para obtener la calificación final de cada asignatura el participante deberá
                acreditar los exámenes o los requisitos que cada asignatura le solicite.
              </p>

              <div className="bg-green-50 rounded-lg p-6 mb-8">
                <h3 className="text-xl font-bold text-[#0b742f] mb-4">Proceso Semestral</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Al final de cada semestre y una vez acreditando el 100% de los cursos activos, el participante
                  solicitará darse de alta al siguiente calendario. En los casos de que el participante no acredite
                  la asignatura podrá solicitar examen extraordinario mismo que presentará en un lapso de 3 días
                  después de conocer los resultados, esto con la idea de que no pierda tiempo para presentar la
                  siguiente asignatura.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Aulas Virtuales */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-xl p-8 md:p-12 border border-blue-100">
            <div className="flex items-center gap-3 mb-8">
              <Globe className="w-10 h-10 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Aulas Virtuales</h2>
            </div>

            <p className="text-base leading-relaxed text-gray-700 mb-8">
              Sigue día a día todas las actividades propuestas por el equipo docente. Este seguimiento es
              todavía más fundamental para superar tus estudios. Plantea todas tus preguntas a tu Tutor de
              Apoyo en Red.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-blue-700 mb-4">Un apoyo para tus estudios de grado y posgrado</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Tanto si estás cursando una licenciatura, maestría como un doctorado o cualquiera de los
                  programas y cursos de educación permanente (programas de especialización, desarrollo profesional, etc.)
                  el aula virtual CENID será tu herramienta de comunicación con otros alumnos y el lugar en el que
                  encontrarás materiales complementarios para el estudio.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-blue-700 mb-4">Conecta con tus compañeros/as</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Utiliza los foros y chat de tu aula virtual para solucionar tus dudas o conocer a gente que
                  comparte tus mismos estudios. Comunícate con tus profesores tutores de manera ágil y debate
                  sobre todo lo que te interese.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-blue-700 mb-4">Toda la información y materiales actualizados</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Desde los últimos apuntes del equipo docente o la fecha del examen hasta el evento próximo a
                  realizarse, no te pierdas nada, te informamos de todo puntualmente en el aula virtual.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-blue-700 mb-4">Accede desde el primer momento</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  El acceso a los cursos virtuales se realiza desde el portal web del CENID, introduciendo el
                  identificador de usuario y la contraseña que se proporcionan al estudiante en el momento de
                  la matriculación.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Requisitos */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <FileText className="w-10 h-10 text-[#e87729]" />
              <h2 className="text-2xl font-bold text-white">Requisitos</h2>
            </div>

            <p className="text-base leading-relaxed text-white/95 mb-8">
              Para poder inscribir a cada uno de nuestros alumnos ante SEP, es necesario contar la siguiente documentación.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Estudiantes Mexicanos */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-bold text-green-100 mb-6">Para alumnos nacionales:</h3>
                <p className="text-base text-white/90 mb-4">
                  Presentar y entregar al Departamento de Control Escolar la siguiente documentación en original y dos fotocopias:
                </p>
                <ul className="space-y-2 text-base text-white/90">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Acta de nacimiento</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Clave Única de Registro de Población (CURP)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Copia de Identificación oficial</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Certificado Total del grado inmediato anterior (original o copia certificada)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Cédula profesional y/o Título de Grado inmediato anterior</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Solicitud de inscripción llena</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Proyecto de investigación</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Pago de aranceles de inscripción semestral y primera colegiatura</span>
                  </li>
                </ul>
              </div>

              {/* Estudiantes Extranjeros */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-bold text-green-100 mb-6">Para Alumnos Extranjeros:</h3>
                <p className="text-base text-white/90 mb-4">
                  Presentar y entregar al Departamento de Control Escolar la siguiente documentación:
                </p>
                <ul className="space-y-2 text-base text-white/90">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Acta de nacimiento apostillada o legalizada</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Copia de Identificación oficial vigente</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Título de Grado apostillado o legalizado</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Certificado de estudios apostillado o legalizado</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Resolución de Revalidación, Equivalencia o Dictamen Técnico</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Proyecto de investigación</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Solicitud de inscripción con fotografía y firma</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-200 flex-shrink-0 mt-0.5" />
                    <span>Pago de aranceles correspondientes</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mt-8">
              <h3 className="text-xl font-bold text-green-100 mb-4">Entrega de documentación</h3>
              <p className="text-white/95 mb-4">
                Es indispensable que todos los alumnos envíen su documentación en original o copias certificadas
                ante Notario Público dentro del periodo de inscripción a la institución, de lo contrario no podrán
                ser inscritos ante la Secretaría de Educación Pública.
              </p>
              <p className="text-green-200 font-semibold">
                📍 Envío de documentos: Independencia #329, Col. Zapopan Centro, C.P. 45100, Zapopan, Jal.
              </p>
            </div>
          </div>
        </section>

        {/* Proceso de Inscripción */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-blue-500">
            <div className="flex items-center gap-3 mb-8">
              <Calendar className="w-10 h-10 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Proceso de Inscripción</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-blue-700 mb-4">Nuevo Ingreso</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-bold">1</span>
                    <span className="text-base text-gray-700">Solicitar el ingreso de acuerdo con el calendario de trámites</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-bold">2</span>
                    <span className="text-base text-gray-700">Haber terminado íntegramente el ciclo de estudios anterior requerido</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-bold">3</span>
                    <span className="text-base text-gray-700">Completar la SOLICITUD DE INSCRIPCIÓN y enviar documentación digitalizada a: <strong>inscripcionespostgrados@cenid.org</strong></span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-bold">4</span>
                    <span className="text-base text-gray-700">Realizar el pago de inscripción y colegiatura. Enviar comprobante a: <strong>inscripcionespostgrados@cenid.org</strong></span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-bold">5</span>
                    <span className="text-base text-gray-700">Recibir usuario y contraseña para acceso a la plataforma de estudios por correo electrónico</span>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#0b742f] mb-4">Re-ingreso</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="bg-[#0b742f] text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-bold">1</span>
                    <span className="text-base text-gray-700">Hacer el pago de re-inscripción</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-[#0b742f] text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-bold">2</span>
                    <span className="text-base text-gray-700">Enviar el comprobante de pago al correo: <strong>inscripcionespostgrados@cenid.org</strong></span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-[#0b742f] text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-bold">3</span>
                    <span className="text-base text-gray-700">Recibir información y acceso a la plataforma educativa por correo electrónico</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Revalidación, Acreditaciones y Equivalencias */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-xl p-8 md:p-12 border border-amber-200">
            <div className="flex items-center gap-3 mb-8">
              <Award className="w-10 h-10 text-amber-600" />
              <h2 className="text-2xl font-bold text-gray-900">Revalidación, Acreditaciones y Equivalencias</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Revalidación */}
              <div className="bg-white rounded-lg p-6 border border-amber-200">
                <h3 className="text-xl font-bold text-amber-700 mb-4">Revalidación</h3>
                <p className="text-base text-gray-700 mb-4">
                  Revalidación de estudios es la validez oficial que otorga la SEP a los estudios realizados en
                  instituciones que no forman parte del Sistema Educativo Nacional. (Se refiere a estudios hechos en el extranjero)
                </p>
                <h4 className="font-bold text-amber-600 mb-3">Requisitos para revalidación parcial:</h4>
                <ul className="text-base text-gray-600 space-y-2">
                  <li>• Certificado parcial de estudios que pretenda revalidar</li>
                  <li>• Plan de estudios que pretenda revalidar</li>
                  <li>• Programas, temarios u otra documentación que exprese los contenidos de cada unidad de enseñanza aprendizaje</li>
                  <li>• Oficio dirigido a la Comisión de Revalidación del CENID</li>
                  <li>• Solicitud de pago de revalidación debidamente liquidado</li>
                </ul>
                <p className="text-base text-gray-500 mt-3">
                  Los documentos deben presentarse certificados y legalizados; en caso de estar en idioma distinto al español,
                  se deberá acompañar la traducción realizada por perito autorizado.
                </p>
              </div>

              {/* Equivalencia */}
              <div className="bg-white rounded-lg p-6 border border-amber-200">
                <h3 className="text-xl font-bold text-amber-700 mb-4">Equivalencia</h3>
                <p className="text-base text-gray-700 mb-4">
                  Equivalencias de estudios es la declaración de la igualdad académica entre los estudios de tipo
                  medio superior y superior realizados en instituciones que forman parte del Sistema Educativo Nacional
                  y los que se imparten en el CENID (Se refiere a estudios hechos en instituciones educativas dentro del país que no pertenecen al CENID)
                </p>
                <h4 className="font-bold text-amber-600 mb-3">Requisitos:</h4>
                <ul className="text-base text-gray-600 space-y-2">
                  <li>• Certificado parcial de estudios que pretenda revalidar</li>
                  <li>• Plan de estudios que pretenda revalidar</li>
                  <li>• Programas, temarios u otra documentación que exprese los contenidos</li>
                  <li>• Oficio dirigido a la Comisión de Revalidación del CENID</li>
                  <li>• Solicitud de pago debidamente liquidado</li>
                </ul>
              </div>

              {/* Acreditación */}
              <div className="bg-white rounded-lg p-6 border border-amber-200">
                <h3 className="text-xl font-bold text-amber-700 mb-4">Acreditación</h3>
                <p className="text-base text-gray-700 mb-4">
                  Acreditación de estudios es la determinación de las igualdades académicas entre las unidades de
                  enseñanza-aprendizaje correspondientes a los planes y programas de estudios del propio CENID.
                  (Se refiere a programas educativos pertenecientes al mismo CENID)
                </p>
                <h4 className="font-bold text-amber-600 mb-3">Requisitos:</h4>
                <ul className="text-base text-gray-600 space-y-2">
                  <li>• Certificado parcial de estudios que pretenda revalidar</li>
                  <li>• Oficio dirigido a la Comisión de Revalidación del CENID</li>
                  <li>• Solicitud de pago de revalidación, acreditación o equivalencias, debidamente liquidado</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Preguntas Frecuentes */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-xl p-8 md:p-12 border border-purple-100">
            <div className="flex items-center gap-3 mb-8">
              <Target className="w-10 h-10 text-purple-600" />
              <h2 className="text-2xl font-bold text-gray-900">Preguntas Frecuentes</h2>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Cómo se estudia por Internet?</h3>
                <p className="text-base text-gray-700 leading-relaxed mb-3">
                  Se trabaja 100% vía Internet, a través de un portal personalizado donde el alumno encontrará
                  el plan de trabajo a desarrollar de cada materia, así como los recursos complementarios necesarios
                  para realizar su aprendizaje. Esto brinda al participante una total flexibilidad de tiempo y
                  espacio en el estudio de cada curso.
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  Los grupos son pequeños máximo de 20 participantes por sección, dirigidos por un profesor titular,
                  lo que facilita una interacción estrecha con asesores.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Cuánto dura la maestría y doctorado?</h3>
                <p className="text-base text-gray-700 leading-relaxed mb-3">
                  Todos nuestros programas de maestría cuentan entre 3 ó 4 materias por semestre y se pueden cursar
                  de forma ideal, en un periodo de 2 años y seis meses.
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  Todos nuestros programas de doctorado cuentan entre 3 ó 4 materias por semestre y se pueden cursar
                  de forma ideal, en un periodo de 2 años y seis meses.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Debo estar conectado a mis cursos en algún tiempo en particular del día?</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  La dinámica de trabajo es primordialmente asíncrona, por lo que el estudiante no tiene la obligación
                  de estar conectado en algún momento en específico del día.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Cuáles son los canales de comunicación con los que cuento para estar en contacto con profesores y estudiantes?</h3>
                <p className="text-base text-gray-700 leading-relaxed mb-3">
                  Los medios de interacción entre profesores y estudiantes son:
                </p>
                <ul className="space-y-1 text-gray-600">
                  <li>• Correo electrónico</li>
                  <li>• Foros de discusión</li>
                  <li>• Chats (asesorías)</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Cada cuando tengo que ir al campus del CENID?</h3>
                <p className="text-base text-gray-700 leading-relaxed mb-3">
                  Los programas del CENID cuentan con una infraestructura administrativa que representa los intereses
                  del estudiante a distancia, lo cual garantiza el no tener que desplazarse a las instalaciones durante
                  el desarrollo de su programa. La única vez que solicitamos la presencia del estudiante es para la firma
                  de su título de graduación, ya que por la delicadeza del trámite, es personal e intransferible.
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  Adicionalmente puede asistir, si así lo desea, a la ceremonia de graduación efectuada en el periodo de verano.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Los alumnos de Educación a Distancia tendrán los mismos derechos que los alumnos presenciales?</h3>
                <p className="text-base text-gray-700 leading-relaxed mb-3">
                  Se contempla brindarle al alumno de educación a distancia todo el apoyo para que logre su aprendizaje sin necesidad de estar físicamente en el campus.
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  Por ejemplo, la necesidad de materiales de biblioteca será cubierta a través de los servicios biblioteca digital con acceso remoto y el contacto y asesorías con los profesores y con los profesores tutores será vía correo electrónico, foros de discusión y si el profesor lo considera prudente puede incluirse alguna plática con él en tiempo real, pero siempre serán recursos disponibles en Internet.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Es lo mismo que cursar una maestría o doctorado presencial?</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Investigadores y profesionales de la educación y de la tecnología se han encargado de desarrollar y afinar un modelo pedagógico para ser aplicado en la educación a distancia, de tal forma que garantice los mismos resultados que una modalidad presencial. Sin embargo la principal diferencia estriba en que una gran parte de la responsabilidad del aprendizaje recae en el propio alumno por lo que se requiere que éste cuente con una serie de habilidades de autoaprendizaje y autorregulación que le permitan desempeñarse exitosamente en sus estudios de posgrado.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Cuáles son los fundamentos de su modelo de aprendizaje?</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Nuestro modelo se encuentra basado en una filosofía constructivista, donde se busca un aprendizaje significativo en la persona a partir de su propia experiencia laboral a través de una situación simulada. Para mayor información puedes revisar la página de nuestro modelo educativo.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Es posible revalidar materias de otras licenciaturas, maestrías o doctorados no concluidas para continuar los estudios en esta modalidad?</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Es posible revalidar cursos aprobados en instituciones que pertenezcan al sistema nacional de educación, cuyo contenido sea equiparable a los pertenecientes al programa que se desea cursar.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Existe la posibilidad de estudiar becado?</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Actualmente no se tiene disponibilidad de becas para el caso de estudiantes no presenciales. En el CENID, tiene por política el manejar siempre el mejor precio posible en todos sus programas a distancia. El costo de la unidad académica de estudio a distancia es 60% más económica que su equivalente presencial, lo cual representa una ventaja derivada del compromiso social.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">Si ya decidí estudiar la maestría y doctorado a distancia en el CENID, ¿Cuál es el siguiente paso?</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  El primer paso es hacer el trámite de admisión. Posteriormente nos contactaremos contigo indicándote el resto de los requisitos.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Qué se espera de mí como estudiante de un programa de educación a distancia?</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Se sabe que las personas que toman un programa de educación a distancia provienen de una gran cantidad de disciplinas, edades, culturas así como con diversas expectativas y experiencias laborales. La elección de un programa en esta modalidad obedece principalmente a consideraciones económicas, familiares o laborales. Por todo lo anterior se espera de un alumno una gran motivación para lograr el éxito en sus estudios, asimismo tener la suficiente disciplina y compromiso para combinar los tiempos de estudio con sus obligaciones familiares y laborales; y por último, tener la capacidad de adaptación necesaria para incorporarse a un nuevo ambiente de aprendizaje.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Cuánto tiempo de estudio le dedico a cada materia por semana?</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Cada materia tiene una carga académica de 4 unidades, lo que implica que el estudiante debe dedicarle
                  un tiempo aproximado de 8 horas de estudio por semana.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Habrá asesorías?</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Sí, por parte de los profesores vía correo electrónico, foros de discusión o salones virtuales de plática o "chats". Para el caso de asesorías no académicas, es decir en cualquier trámite administrativo, queja o sugerencia podrás dirigirte directamente con tu coordinador administrativo.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Cómo se efectúan las actividades de evaluación dentro de cada materia?</h3>
                <p className="text-base text-gray-700 leading-relaxed mb-3">
                  Las evaluaciones se realizan a partir del cumplimiento de los requerimientos que marca cada actividad de acuerdo a la metodología establecida en el plan de trabajo.
                </p>
                <p className="text-base text-gray-700 leading-relaxed mb-3">
                  Primordialmente las actividades se orientan a:
                </p>
                <ul className="space-y-1 text-gray-600">
                  <li>• Solucionar un problema</li>
                  <li>• Tomar una decisión</li>
                  <li>• Realizar un diseño</li>
                  <li>• Emitir un juicio</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-bold text-purple-700 mb-3">¿Cuál es la calificación mínima aprobatoria de cada materia en maestrías y doctorados?</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  La calificación mínima aprobatoria es de 8.0 (ocho).
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Servicios Académicos Completos */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-[#e87729]">
            <div className="flex items-center gap-3 mb-8">
              <Award className="w-10 h-10 text-[#e87729]" />
              <h2 className="text-2xl font-bold text-gray-900">Servicios Académicos</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-orange-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#e87729] mb-4">Tutorías en Línea</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  A través de Internet tendrás acceso a tutorías on-line, que no es otra cosa que poder trabajar
                  con tu Tutor a través de la computadora en cualquier momento y lugar.
                </p>
              </div>

              <div className="bg-orange-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#e87729] mb-4">Materiales Docentes</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  El material didáctico denominado Guías Autoinstruccionales, constituye el instrumento fundamental
                  para tu estudio en CENID. Estas Guías Autoinstruccionales, te orientarán en cuanto a métodos de
                  estudio y contenidos de cada materia.
                </p>
              </div>

              <div className="bg-orange-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#e87729] mb-4">Sistemas de Evaluación</h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  Pruebas de evaluación a distancia. El tipo de evaluación será establecido por el equipo docente
                  de la asignatura y puede consistir, entre otras posibilidades, en:
                </p>
                <ul className="text-base text-gray-600 space-y-1">
                  <li>• Prueba objetiva</li>
                  <li>• Ensayos</li>
                  <li>• Trabajo empírico</li>
                  <li>• Estudio de casos</li>
                  <li>• Comentario de texto</li>
                  <li>• Prácticas de laboratorio</li>
                </ul>
                <p className="text-gray-700 text-base mt-3">
                  Serán corregidas por los profesores tutores, según los criterios del equipo docente. El equipo docente
                  podrá solicitar a los estudiantes, cuando lo considere oportuno, la realización de trabajos complementarios.
                </p>
              </div>
            </div>

            {/* Información para estudiantes */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 mb-8">
              <h3 className="text-2xl font-bold text-[#e87729] mb-6">Información para Estudiantes</h3>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-lg font-bold text-blue-700 mb-4">Solicitudes</h4>
                  <p className="text-base text-gray-700 mb-4">
                    El CENID, pone a disposición de estudiantes y docentes todos los tipos de solicitudes y movimientos
                    académicos que sean necesarios dentro de un plazo máximo de 48 horas. Toda solicitud de tipo académico
                    y certificados, deberán canalizarse a través del área de Control Escolar de la Secretaria Académica.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-blue-700 mb-4">Trámite de Constancias</h4>
                  <p className="text-gray-700 mb-3">
                    El CENID a través del área de Control Escolar emite los siguientes documentos escolares:
                  </p>
                  <ul className="text-base text-gray-600 space-y-2">
                    <li>• <strong>Constancia de estudios:</strong> muestra las asignaturas registradas en el periodo escolar vigente</li>
                    <li>• <strong>Informe de calificaciones:</strong> muestra las calificaciones obtenidas en el periodo inmediato anterior</li>
                    <li>• <strong>Informe general de calificaciones:</strong> muestra las asignaturas acreditadas o reprobadas desde el inicio de estudios</li>
                    <li>• <strong>Constancia de créditos y promedio:</strong> proporciona el avance en créditos, promedio general y total de asignaturas cursadas (solo con 50% de avance)</li>
                    <li>• <strong>Certificado parcial de estudios:</strong> únicamente para estudiantes de baja definitiva (requiere 3 fotografías tamaño infantil)</li>
                    <li>• <strong>Carta de pasante:</strong> requiere 100% del plan de estudios, ser estudiante regular, promedio mínimo de 80 (requiere 2 fotografías)</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 mt-6">
                <h4 className="text-lg font-bold text-blue-700 mb-4">Fechas de Solicitud y Tiempo de Entrega</h4>
                <div className="space-y-3">
                  <p className="text-gray-700">
                    Únicamente se reciben solicitudes de constancia de estudios, conforme al calendario académico cuando
                    inicie el periodo escolar que corresponda. En ningún caso se emitirán constancias fuera del periodo escolar.
                  </p>
                  <p className="text-gray-700">
                    Para solicitar documentos, enviar al correo electrónico institucional indicando en el asunto si es para beca o trámite personal.
                  </p>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h5 className="font-bold text-blue-700 mb-2">Datos requeridos en la solicitud:</h5>
                    <ul className="text-base text-gray-600 space-y-1">
                      <li>• Matrícula asignada como estudiante por CENID</li>
                      <li>• Periodo escolar (semestre que cursa)</li>
                      <li>• Programa educativo (nombre de grado) que cursa</li>
                      <li>• Nivel (señalar grado académico)</li>
                    </ul>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 mt-4">
                    <h5 className="font-bold text-amber-700 mb-2">Consideraciones importantes:</h5>
                    <ul className="text-base text-gray-600 space-y-1">
                      <li>• La documentación puede tardar un máximo de 5 días hábiles</li>
                      <li>• Es necesario estar al corriente en pagos y contar con expediente electrónico completo</li>
                      <li>• No se otorgan constancias a estudiantes inactivos o de baja temporal</li>
                      <li>• No se otorgan informes de calificaciones a estudiantes con NP en todas las asignaturas</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Información para asesores */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6">
              <h3 className="text-2xl font-bold text-[#e87729] mb-6">Información para Asesores</h3>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="text-lg font-bold text-green-700 mb-3">Noticias/Eventos</h4>
                  <p className="text-base text-gray-700">
                    Entérese de todas las actividades académicas que ocurren en la Universidad.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4">
                  <h4 className="text-lg font-bold text-green-700 mb-3">Publicaciones</h4>
                  <p className="text-base text-gray-700">
                    Publicaciones importantes locales y mundiales tienen su espacio aquí. Entérese lo que ocurre en la red Global.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4">
                  <h4 className="text-lg font-bold text-green-700 mb-3">Formación Integral</h4>
                  <p className="text-base text-gray-700">
                    CENID, consciente de que la formación integral y continua de sus docentes tiene una propuesta de formación
                    en línea que promueve el desarrollo profesional de docentes. A través de una página de internet se ofrecen
                    una serie de recursos como: material didáctico, tutoriales, cursos, programas de certificación, etc.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 mt-6">
                <h4 className="text-lg font-bold text-green-700 mb-3">SAGA</h4>
                <p className="text-base text-gray-700">
                  En el Sistema de Administración y Gestión Académica los docentes pueden ingresar notas, status y otros registros.
                  Cuenta con los horarios de cada docente por las materias que dicta en el semestre o cuatrimestre en curso.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Convalidación */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl shadow-xl p-8 md:p-12 border border-teal-200">
            <div className="flex items-center gap-3 mb-8">
              <Globe className="w-10 h-10 text-teal-600" />
              <h2 className="text-2xl font-bold text-gray-900">Convalidación</h2>
            </div>

            <div className="bg-white rounded-lg p-6 mb-8">
              <h3 className="text-2xl font-bold text-teal-700 mb-4">Convalide su Titulación Oficial</h3>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Los títulos de postgrado emitidos por el CENID pueden ser homologados por las autoridades educativas de Colombia.
                Es decir, tanto los estudios de pregrado, como los certificados, títulos, diplomas y postgrados académicos oficiales
                se pueden convalidar para que tengan validez legal en el sistema educativo colombiano.
              </p>
              <p className="text-teal-700 font-semibold">
                Aquí podrá resolver todas sus dudas sobre la convalidación de sus títulos.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-teal-700 mb-4">¿Qué es la convalidación?</h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  La convalidación es el reconocimiento que el gobierno colombiano efectúa sobre un título de educación superior
                  otorgado por una institución de educación superior extranjera o por una institución legalmente reconocida por
                  la autoridad competente en el respectivo país para expedir títulos de educación superior.
                </p>
                <p className="text-teal-600 font-semibold">
                  AQUÍ podrá conocer la nueva regulación sobre convalidación de títulos de educación superior publicada por el
                  Ministerio de Educación Nacional de Colombia en el 2018.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-teal-700 mb-4">¿Quién realiza la convalidación y qué costo tiene?</h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  La convalidación es un procedimiento que queda a cargo del estudiante y deberá realizarlo ante el Ministerio
                  de Educación Nacional. Puede consultar las tarifas establecidas por el Ministerio en el SIGUIENTE LINK.
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  El proceso de convalidación podrá tardar de 2 a 4 meses dependiendo del tipo de trámite al que sea asignada su solicitud.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-teal-700 mb-4">¿Qué sucede con los títulos que no se encuentran convalidados y no aparecen en el Ministerio de Educación Nacional?</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  No todas las titulaciones de maestría, doctorado o pregrado aparecen en el sistema dispuesto por el Ministerio
                  de Educación Nacional. Cada convalidación depende del proceso que realice el estudiante, y si es un programa reciente
                  el cuál no tiene aún egresado no tendrá, en ese momento, la convalidación respectiva. Eso no quiere decir que el
                  programa no sea reconocido por el Ministerio de Educación Nacional.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-teal-700 mb-4">¿Qué es un acuerdo de reconocimiento mutuo (ARM)?</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Es el reconocimiento formal por parte de los gobiernos de dos países sobre la compatibilidad de sus Sistemas Educativos.
                  Esto resulta en el otorgamiento de beneficios mutuos para todos los países que están amparados bajo el acuerdo.
                  La existencia de un ARM entre dos países no implica la convalidación automática de un título.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contacto */}
        <section>
          <div className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <Mail className="w-10 h-10 text-[#e87729]" />
              <h2 className="text-2xl font-bold text-white">Contacto e Información</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-green-100 mb-4">Correos de Contacto</h3>
                <div className="space-y-3">
                  <a
                    href="mailto:inscripcionespostgrados@cenid.org"
                    className="flex items-center gap-2 text-green-200 hover:text-white transition-colors duration-300"
                  >
                    <Mail className="w-4 h-4" />
                    <span>inscripcionespostgrados@cenid.org</span>
                  </a>
                  <p className="text-white/90 text-sm">
                    Para inscripciones, re-inscripciones y envío de documentación
                  </p>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-green-100 mb-4">Plataforma de Estudios</h3>
                <div className="space-y-3">
                  <a
                    href="https://postgrados.cenid.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-green-200 hover:text-white transition-colors duration-300"
                  >
                    <Globe className="w-4 h-4" />
                    <span>postgrados.cenid.org</span>
                  </a>
                  <p className="text-white/90 text-sm">
                    Acceso a cursos en línea y materiales de estudio
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Aspirantes;